// HUD do assistente (Fase 3). Overlay por cima do grafo (grafo continua atrás).
// Reator central reativo a áudio + console de eventos + voz tempo-real + texto.
// Não muda o flow do grafo — é uma lente. Toggle via store.assistantOpen.
import type * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { X, Mic, Square, Send } from 'lucide-react';
import { useStore } from '../store/useStore.js';
import { useRealtimeVoice, type VoiceStatus } from '../ai/useRealtimeVoice.js';
import { runAgent } from '../ai/agentClient.js';
import { applyAgentActions } from '../ai/agentActions.js';
import { VoiceReactor } from './VoiceReactor.js';

interface LogEntry {
  id: string;
  role: 'user' | 'assistant' | 'action';
  text: string;
}

interface AgentEvent {
  id: string;
  kind: string;
  tool: string | null;
  summary: string;
  createdAt: string;
}

function evTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

const STATUS_LABEL: Record<VoiceStatus, string> = {
  idle: 'Em espera',
  connecting: 'Conectando…',
  listening: 'Ouvindo',
  thinking: 'Processando…',
  speaking: 'Respondendo…',
  error: 'Erro',
};

export function AssistantHUD() {
  const open = useStore((s) => s.assistantOpen);
  const setOpen = useStore((s) => s.setAssistantOpen);
  const workspaceId = useStore((s) => s.workspaceId);
  const nodeCount = useStore((s) => s.nodes.length);
  const linkCount = useStore((s) => s.links.length);

  const [log, setLog] = useState<LogEntry[]>([]);
  const [events, setEvents] = useState<AgentEvent[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [handsFree, setHandsFree] = useState(false);
  const handsFreeRef = useRef(handsFree);
  handsFreeRef.current = handsFree;
  // Modo captura (brain-dump): acumula a fala e só organiza no gatilho.
  const [captureMode, setCaptureMode] = useState(false);
  const captureRef = useRef(captureMode);
  captureRef.current = captureMode;
  const bufferRef = useRef<string[]>([]);
  const [buffer, setBuffer] = useState<string[]>([]);
  const logEndRef = useRef<HTMLDivElement>(null);

  async function loadEvents() {
    if (!workspaceId) return;
    try {
      const res = await fetch(`/api/ai/agent/events?workspaceId=${workspaceId}&limit=30`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = (await res.json()) as { events?: AgentEvent[] };
      setEvents(Array.isArray(data.events) ? data.events : []);
    } catch {
      /* feed é best-effort */
    }
  }

  function buildContext(): string | undefined {
    const s = useStore.getState();
    const recent = s.nodes
      .slice(-12)
      .map((n) => n.label)
      .filter(Boolean);
    const sel = s.selectedId ? s.nodes.find((n) => n.id === s.selectedId)?.label : null;
    const parts: string[] = [];
    if (recent.length) parts.push(`Nós existentes (mais recentes por último): ${recent.join(', ')}.`);
    if (sel) parts.push(`Nó em foco: "${sel}".`);
    return parts.length ? parts.join(' ') : undefined;
  }

  async function turn(q: string): Promise<string> {
    if (!workspaceId) return '';
    // Memória curta: últimos turnos de texto (antes de empurrar o atual).
    const history = log
      .filter((e) => e.role === 'user' || e.role === 'assistant')
      .slice(-6)
      .map((e) => ({ role: e.role as 'user' | 'assistant', content: e.text }));
    const ctx = buildContext();
    setLog((l) => [...l, { id: crypto.randomUUID(), role: 'user', text: q }]);
    const { reply, actions } = await runAgent(workspaceId, q, ctx, history);
    const summary = applyAgentActions(actions);
    if (reply) setLog((l) => [...l, { id: crypto.randomUUID(), role: 'assistant', text: reply }]);
    if (summary) setLog((l) => [...l, { id: crypto.randomUUID(), role: 'action', text: summary }]);
    void loadEvents();
    return reply || '';
  }

  // Flush do brain-dump: manda o despejo acumulado pro agente estruturar.
  async function flushCapture() {
    const text = bufferRef.current.join(' ').trim();
    bufferRef.current = [];
    setBuffer([]);
    if (!text) return;
    const q = `Estruture este despejo de ideias no grafo: crie nós, subnós, tarefas e anotações coerentes, aninhando o que fizer sentido. Use list_nodes para não duplicar. Despejo do usuário: "${text}"`;
    setBusy(true);
    try {
      await turn(q);
    } catch (err) {
      setLog((l) => [...l, { id: crypto.randomUUID(), role: 'assistant', text: 'Erro: ' + (err as Error).message }]);
    } finally {
      setBusy(false);
    }
  }

  const voice = useRealtimeVoice({
    workspaceId,
    onUtterance: async (raw) => {
      let t = raw.trim();
      const low = t.toLowerCase();

      // Comando de parada: barge-in já cortou o TTS; não roda o agente.
      if (/^(parar|pare|cancela|cancelar|sil[êe]ncio|chega)\b/.test(low) && t.split(/\s+/).length <= 2) {
        return '';
      }

      // Modo mãos-livres: só age se a fala contiver a wake word.
      if (handsFreeRef.current) {
        const m = low.match(/\b(synapse|ultron|assistente)\b/);
        if (!m) return '';
        t = t.slice(low.indexOf(m[0]) + m[0].length).replace(/^[\s,:.!?]+/, '').trim();
        if (!t) return '';
      }

      // Modo captura: acumula; só organiza no gatilho de voz.
      if (captureRef.current) {
        const lowT = t.toLowerCase();
        if (/\b(organiza|organizar|estrutura|estruturar|monta isso|monta tudo|pode criar|cria isso)\b/.test(lowT)) {
          await flushCapture();
        } else {
          bufferRef.current = [...bufferRef.current, t];
          setBuffer(bufferRef.current);
        }
        return '';
      }

      try {
        await turn(t);
      } catch (err) {
        setLog((l) => [...l, { id: crypto.randomUUID(), role: 'assistant', text: 'Erro: ' + (err as Error).message }]);
      }
      return ''; // execute-only: sem TTS, confirmação é visual (console + grafo)
    },
  });

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [log]);

  useEffect(() => {
    if (!open || !workspaceId) return;
    void loadEvents();
    const iv = window.setInterval(() => void loadEvents(), 5000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workspaceId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function close() {
    voice.stop();
    setOpen(false);
  }

  async function submitText(e: React.FormEvent) {
    e.preventDefault();
    const q = text.trim();
    if (!q || busy) return;
    setText('');
    setBusy(true);
    try {
      await turn(q);
    } catch (err) {
      setLog((l) => [...l, { id: crypto.randomUUID(), role: 'assistant', text: 'Erro: ' + (err as Error).message }]);
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  const voiceActive = voice.status !== 'idle' && voice.status !== 'error';

  return (
    <div style={backdropStyle} role="dialog" aria-label="Assistente de voz">
      {/* Telemetria (canto superior esquerdo) */}
      <div style={telemetryStyle}>
        <div style={telTitleStyle}>SYNAPSE · ASSISTENTE</div>
        <div style={telRowStyle}><span style={telKeyStyle}>NÓS</span><span style={telValStyle}>{nodeCount}</span></div>
        <div style={telRowStyle}><span style={telKeyStyle}>LIGAÇÕES</span><span style={telValStyle}>{linkCount}</span></div>
        <div style={telRowStyle}><span style={telKeyStyle}>ESTADO</span><span style={telValStyle}>{STATUS_LABEL[voice.status]}</span></div>
      </div>

      {/* Atividade persistida (canto inferior esquerdo) */}
      <div style={activityStyle}>
        <div style={consoleTitleStyle}>ATIVIDADE</div>
        <div style={consoleBodyStyle} className="hud-console">
          {events.length === 0 && <div style={consoleEmptyStyle}>Sem eventos ainda.</div>}
          {events.map((e) => (
            <div key={e.id} style={evRowStyle}>
              <span style={evTimeStyle}>{evTime(e.createdAt)}</span>
              <span
                style={{
                  color: e.kind === 'action' ? 'var(--neon)' : 'var(--text)',
                  opacity: e.kind === 'utterance' ? 0.75 : 1,
                }}
              >
                {e.summary}
              </span>
            </div>
          ))}
        </div>
      </div>

      <button onClick={close} aria-label="Fechar assistente" style={closeStyle}>
        <X size={18} />
      </button>

      {/* Reator central */}
      <div style={centerStyle}>
        <VoiceReactor status={voice.status} getLevel={voice.getLevel} size={320} />
        <div style={statusTextStyle}>{STATUS_LABEL[voice.status]}</div>
        {voice.transcript && <div style={liveTranscriptStyle}>“{voice.transcript}”</div>}
        {voiceActive && handsFree && <div style={hintStyle}>Mãos-livres · diga “Synapse” + comando</div>}
        {captureMode && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginTop: 2 }}>
            <div style={hintStyle}>
              Captura · {buffer.length} trecho(s){buffer.length ? ' — diga “organiza” ou clique' : ' — fale sua ideia'}
            </div>
            {buffer.length > 0 && (
              <button type="button" onClick={() => void flushCapture()} style={organizeBtnStyle}>
                Organizar agora
              </button>
            )}
          </div>
        )}
        {voice.error && <div style={errStyle}>{voice.error}</div>}
      </div>

      {/* Console de eventos (direita) */}
      <div style={consoleStyle}>
        <div style={consoleTitleStyle}>CONSOLE</div>
        <div style={consoleBodyStyle}>
          {log.length === 0 && (
            <div style={consoleEmptyStyle}>
              Toque no microfone e fale, ou digite.
              <br />
              <br />
              Tente:
              <br />
              • “cria a ideia Lançamento e 3 subtarefas”
              <br />
              • “anota em Lançamento: focar em PIX”
              <br />
              • “liga Marketing em Lançamento”
              <br />
              • “pinta Lançamento de verde”
              <br />
              • “desfaz isso”
              <br />
              <br />
              <b>Captura</b> (ícone de notas): fale a ideia inteira e diga “organiza”.
            </div>
          )}
          {log.map((e) => (
            <div key={e.id} style={logLineStyle(e.role)}>
              <span style={logTagStyle(e.role)}>
                {e.role === 'user' ? 'VOCÊ' : e.role === 'assistant' ? 'ULTRON' : 'AÇÃO'}
              </span>
              <span>{e.text}</span>
            </div>
          ))}
          <div ref={logEndRef} />
        </div>
      </div>

      {/* Controles (rodapé) */}
      <div style={controlsStyle}>
        <button
          type="button"
          onClick={() => (voiceActive || voice.status === 'connecting' ? voice.stop() : voice.start())}
          title={voiceActive ? 'Encerrar voz' : 'Falar'}
          style={micBtnStyle(voiceActive)}
        >
          {voiceActive || voice.status === 'connecting' ? <Square size={20} /> : <Mic size={22} />}
        </button>
        <button
          type="button"
          onClick={() => setHandsFree((v) => !v)}
          title={'Mãos-livres: só age quando você diz “Synapse” + comando'}
          aria-pressed={handsFree}
          style={handsFreeBtnStyle(handsFree)}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>hearing</span>
        </button>
        <button
          type="button"
          onClick={() => setCaptureMode((v) => !v)}
          title={captureMode ? 'Captura ON — fale a ideia e diga “organiza” pra montar' : 'Modo captura (brain-dump)'}
          aria-pressed={captureMode}
          style={handsFreeBtnStyle(captureMode)}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>note_stack</span>
        </button>
        <form onSubmit={submitText} style={textFormStyle}>
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Ou digite um comando…"
            disabled={busy}
            style={textInputStyle}
          />
          <button type="submit" disabled={busy || !text.trim()} aria-label="Enviar" style={sendStyle(busy || !text.trim())}>
            <Send size={16} />
          </button>
        </form>
      </div>

      <style>{`
        @keyframes hud-in { from { opacity: 0; } to { opacity: 1; } }
        .hud-console::-webkit-scrollbar { width: 6px; }
        .hud-console::-webkit-scrollbar-thumb { background: rgba(var(--neon-rgb), 0.2); border-radius: 999px; }
      `}</style>
    </div>
  );
}

const backdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 200,
  background: 'radial-gradient(ellipse at center, rgba(2,8,16,0.72), rgba(2,6,12,0.92))',
  backdropFilter: 'blur(8px) saturate(120%)',
  WebkitBackdropFilter: 'blur(8px) saturate(120%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'var(--font-display)',
  animation: 'hud-in .25s ease-out',
  overflow: 'hidden',
};

const telemetryStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(var(--topbar-h, 54px) + 16px)',
  left: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: '12px 14px',
  borderRadius: 12,
  border: '1px solid rgba(var(--neon-rgb), 0.18)',
  background: 'rgba(var(--neon-rgb), 0.04)',
  minWidth: 150,
};

const telTitleStyle: React.CSSProperties = {
  fontSize: 9.5,
  letterSpacing: '0.18em',
  color: 'var(--neon)',
  fontWeight: 700,
  marginBottom: 4,
};

const telRowStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 16 };
const telKeyStyle: React.CSSProperties = { fontSize: 10, letterSpacing: '0.1em', color: 'var(--muted)' };
const telValStyle: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' };

const closeStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(var(--topbar-h, 54px) + 16px)',
  right: 20,
  width: 34,
  height: 34,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid var(--panel-border)',
  borderRadius: 10,
  color: 'var(--muted)',
  cursor: 'pointer',
};

const centerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 6,
  maxWidth: '60vw',
};

const statusTextStyle: React.CSSProperties = {
  fontSize: 12,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: 'var(--neon)',
  fontWeight: 700,
  marginTop: -8,
};

const liveTranscriptStyle: React.CSSProperties = {
  fontSize: 15,
  color: 'var(--text)',
  textAlign: 'center',
  maxWidth: 460,
  lineHeight: 1.5,
  opacity: 0.92,
};

const errStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#ff8fa0',
  textAlign: 'center',
  maxWidth: 420,
};

const hintStyle: React.CSSProperties = {
  fontSize: 10.5,
  letterSpacing: '0.08em',
  color: 'var(--muted)',
  textTransform: 'uppercase',
};

const organizeBtnStyle: React.CSSProperties = {
  padding: '8px 18px',
  borderRadius: 999,
  border: '1px solid var(--neon)',
  background: 'var(--neon)',
  color: '#02131a',
  fontWeight: 700,
  fontSize: 12,
  cursor: 'pointer',
  boxShadow: '0 0 18px rgba(var(--neon-rgb), 0.4)',
};

function handsFreeBtnStyle(active: boolean): React.CSSProperties {
  return {
    width: 44,
    height: 44,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    border: `1px solid ${active ? 'var(--neon)' : 'var(--panel-border)'}`,
    background: active ? 'rgba(var(--neon-rgb), 0.16)' : 'rgba(255,255,255,0.04)',
    color: active ? 'var(--neon)' : 'var(--muted)',
    cursor: 'pointer',
    transition: 'all .15s',
  };
}

const consoleStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(var(--topbar-h, 54px) + 16px)',
  right: 20,
  bottom: 96,
  width: 300,
  maxWidth: '32vw',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 12,
  border: '1px solid rgba(var(--neon-rgb), 0.16)',
  background: 'rgba(4,10,18,0.55)',
  overflow: 'hidden',
  marginTop: 52,
};

const consoleTitleStyle: React.CSSProperties = {
  fontSize: 9.5,
  letterSpacing: '0.18em',
  color: 'var(--neon)',
  fontWeight: 700,
  padding: '10px 12px',
  borderBottom: '1px solid var(--panel-border)',
};

const consoleBodyStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  scrollbarWidth: 'thin',
};

const consoleEmptyStyle: React.CSSProperties = {
  fontSize: 11.5,
  color: 'var(--muted)',
  lineHeight: 1.6,
};

function logLineStyle(role: LogEntry['role']): React.CSSProperties {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    fontSize: 12,
    lineHeight: 1.5,
    color: role === 'action' ? 'var(--muted)' : 'var(--text)',
    fontStyle: role === 'action' ? 'italic' : 'normal',
  };
}

function logTagStyle(role: LogEntry['role']): React.CSSProperties {
  const c = role === 'user' ? '#39ff14' : role === 'assistant' ? 'var(--neon)' : 'var(--muted)';
  return { fontSize: 8.5, letterSpacing: '0.14em', fontWeight: 700, color: c };
}

const activityStyle: React.CSSProperties = {
  position: 'absolute',
  left: 20,
  top: 'calc(var(--topbar-h, 54px) + 150px)',
  bottom: 96,
  width: 260,
  maxWidth: '28vw',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 12,
  border: '1px solid rgba(var(--neon-rgb), 0.16)',
  background: 'rgba(4,10,18,0.55)',
  overflow: 'hidden',
};

const evRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  fontSize: 11.5,
  alignItems: 'baseline',
  lineHeight: 1.4,
};

const evTimeStyle: React.CSSProperties = {
  fontSize: 9.5,
  color: 'var(--muted)',
  fontVariantNumeric: 'tabular-nums',
  flexShrink: 0,
};

const controlsStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: 24,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: 'min(560px, 80vw)',
};

function micBtnStyle(active: boolean): React.CSSProperties {
  return {
    width: 52,
    height: 52,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    border: `1px solid ${active ? 'var(--neon)' : 'rgba(var(--neon-rgb), 0.4)'}`,
    background: active ? 'var(--neon)' : 'rgba(var(--neon-rgb), 0.12)',
    color: active ? '#02131a' : 'var(--neon)',
    cursor: 'pointer',
    boxShadow: active ? '0 0 24px rgba(var(--neon-rgb), 0.5)' : '0 0 14px rgba(var(--neon-rgb), 0.25)',
    transition: 'all .15s',
  };
}

const textFormStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 6px 6px 14px',
  borderRadius: 14,
  border: '1px solid var(--panel-border)',
  background: 'rgba(8,14,22,0.7)',
};

const textInputStyle: React.CSSProperties = {
  flex: 1,
  height: 36,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  color: 'var(--text)',
  fontSize: 13,
  fontFamily: 'inherit',
};

function sendStyle(disabled: boolean): React.CSSProperties {
  return {
    width: 36,
    height: 36,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    border: 'none',
    background: disabled ? 'rgba(var(--neon-rgb),0.12)' : 'var(--neon)',
    color: disabled ? 'rgba(var(--neon-rgb),0.5)' : '#02131a',
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}
